/**
 * Simulated QuickBooks endpoints Part 7 needs that `src/demo/api/router.ts`
 * does not answer yet: `/api/qb-ebitda-trailing`, `/api/qb-budget-detail`,
 * `/api/qb-job-pnl`, `/api/qb-payments`, `/api/qb-invoiced-jobs` and
 * `/api/gmb-reviews`.
 *
 * Everything is DERIVED from the production snapshot (`job_invoices`,
 * `v_jobs_full`, `time_clock_snapshots`, `profiles`) and the real
 * `public/data/budget-2026.json`, through the same helpers the existing
 * generators use (`src/demo/simulated/finance.ts`), so these figures cannot
 * disagree with Financials or Collections. Every payload carries
 * `simulated: true` + `demo_note`, and `fetchOrSimulate()` records the hit so
 * the amber SIMULATED DATA banner names QuickBooks on the screen.
 */
import { loadTable } from '~/demo/data';
import { once, seededFloat, seededInt, seededPick } from '~/demo/simulated/prng';
import {
  budgetDoc, monthRange, num, nyDate, pnlAccountsFor, round2, str,
} from '~/demo/simulated/finance';
import { ebitdaAddBacks, ebitda, type AccountLine } from '../rules/finance';

const DEMO_NOTE_TAIL =
  'derived from the job_invoices / v_jobs_full snapshot and the real public/data/budget-2026.json.';

// ── /api/qb-ebitda-trailing ─────────────────────────────────────────────────

export interface EbitdaTrailingPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  /** Trailing-12-month window, most recent month last. */
  months: string[];
  netIncome: number;
  addBacks: { depreciation: number; interest: number; taxes: number; total: number };
  /** Trailing-12-month EBITDA — the Enterprise Value base (spec §2). */
  ebitda: number;
  /** Per-month EBITDA, so the Measurables row can render a trend. */
  byMonth: { month: string; ebitda: number }[];
}

/**
 * `/api/qb-ebitda-trailing` — the TRAILING-12-MONTH EBITDA the Enterprise Value
 * row needs. `/api/qb-budget` only covers the current fiscal year, which is why
 * this is its own endpoint in production too.
 */
export const simulatedEbitdaTrailing = once(async (): Promise<EbitdaTrailingPayload> => {
  const today = nyDate();
  const end = today.slice(0, 7);
  const [y, m] = end.split('-').map(Number);
  const startY = m === 12 ? y : y - 1;
  const startM = m === 12 ? 1 : m + 1;
  const months = monthRange(`${startY}-${String(startM).padStart(2, '0')}`, end);

  const byMonth: { month: string; ebitda: number }[] = [];
  let netIncome = 0;
  const totals = { depreciation: 0, interest: 0, taxes: 0, total: 0 };

  for (const month of months) {
    const acc = await pnlAccountsFor([month], `ebitda-${month}`);
    const ni = acc.totals.income - acc.totals.cogs - acc.totals.expenses;
    const lines: AccountLine[] = acc.expenses.map((l) => ({ name: l.name, amount: l.actual }));
    const add = ebitdaAddBacks(lines);
    netIncome += ni;
    totals.depreciation += add.depreciation;
    totals.interest += add.interest;
    totals.taxes += add.taxes;
    totals.total += add.total;
    byMonth.push({ month, ebitda: round2(ebitda(ni, add)) });
  }

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Trailing-12-month EBITDA = Net Income + Interest + Taxes + D&A, add-backs matched by account-name '
      + `regex with the payroll/FICA/FUTA/SUTA/sales/use/property-tax guard; the underlying P&L is ${DEMO_NOTE_TAIL}`,
    months,
    netIncome: round2(netIncome),
    addBacks: {
      depreciation: round2(totals.depreciation),
      interest: round2(totals.interest),
      taxes: round2(totals.taxes),
      total: round2(totals.total),
    },
    ebitda: round2(netIncome + totals.total),
    byMonth,
  };
});

// ── /api/qb-budget-detail ───────────────────────────────────────────────────

export interface BudgetDetailTxn {
  date: string;
  type: string;
  num: string;
  customer: string;
  memo: string;
  amount: number;
}
export interface BudgetDetailPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  account: string;
  month: string;
  transactions: BudgetDetailTxn[];
  total: number;
}

const TXN_TYPES = ['Invoice', 'Bill', 'Journal Entry', 'Expense', 'Credit Memo'] as const;

/**
 * `/api/qb-budget-detail` — the transaction list behind one account/month cell
 * (Overview Division-by-Month, Financials leaf actuals). Invoice-backed rows use
 * REAL `job_invoices` rows for that month; cost accounts get generated rows that
 * sum exactly to the cell.
 */
export async function simulatedBudgetDetail(
  account: string,
  month: string,
  cellAmount?: number,
): Promise<BudgetDetailPayload> {
  const invoices = await loadTable('job_invoices');
  const jobs = await loadTable('v_jobs_full');
  const customerOf = new Map<string, string>();
  for (const j of jobs) customerOf.set(str(j.job_number), str(j.customer_name));

  const monthRows = invoices.filter((r) => str(r.invoiced_at).slice(0, 7) === month);
  const isIncome = account.trim().startsWith('4');

  let transactions: BudgetDetailTxn[];
  if (isIncome && monthRows.length > 0) {
    transactions = monthRows.slice(0, 60).map((r, i) => ({
      date: str(r.invoiced_at).slice(0, 10),
      type: 'Invoice',
      num: `INV-${String(i + 1).padStart(4, '0')}`,
      customer: `${customerOf.get(str(r.job_number)) || 'Customer'} · ${str(r.job_number)}`,
      memo: `${str(r.division)} services`,
      amount: round2(num(r.invoice_subtotal) || num(r.total_invoiced)),
    }));
  } else {
    const target = cellAmount ?? 0;
    const n = Math.max(1, Math.min(12, seededInt(`detail|${account}|${month}`, 3, 9)));
    const weights = Array.from({ length: n }, (_, i) => seededFloat(`detail|${account}|${month}|${i}`, 0.4, 1.6));
    const sum = weights.reduce((s, w) => s + w, 0) || 1;
    transactions = weights.map((w, i) => ({
      date: `${month}-${String(seededInt(`d|${account}|${month}|${i}`, 1, 28)).padStart(2, '0')}`,
      type: seededPick(`t|${account}|${month}|${i}`, TXN_TYPES) ?? 'Expense',
      num: `${String(seededInt(`n|${account}|${month}|${i}`, 1000, 9999))}`,
      customer: str(seededPick(`c|${account}|${month}|${i}`, jobs.slice(0, 200))?.customer_name) || '—',
      memo: account.replace(/^\d[\d.]*\s*/, '') || 'Transaction',
      amount: round2((target * w) / sum),
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  return {
    ok: true,
    simulated: true,
    demo_note: isIncome
      ? `Income transactions are the real job_invoices rows invoiced in ${month}.`
      : `Cost transactions are generated deterministically and sum to the cell; the P&L behind them is ${DEMO_NOTE_TAIL}`,
    account,
    month,
    transactions,
    total: round2(transactions.reduce((s, t) => s + t.amount, 0)),
  };
}

// ── /api/qb-job-pnl ─────────────────────────────────────────────────────────

export interface JobPnlLine { kind: 'revenue' | 'labor' | 'material'; description: string; amount: number }
export interface JobPnlRow {
  jobNumber: string;
  customer: string;
  division: string;
  revenue: number;
  laborCost: number;
  materialCost: number;
  grossProfit: number;
  grossMarginPct: number;
  closedAt: string | null;
  lines: JobPnlLine[];
}
export interface JobPnlPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  jobs: JobPnlRow[];
}

/**
 * `/api/qb-job-pnl` — per-job revenue / labor / materials. In production this
 * joins QBO Invoices, TSheets timesheets (labor, by jobcode name) and QBO Bills
 * (materials + subs, by `CustomerRef`), matched on the job number. Here revenue
 * and the job list are REAL (`job_invoices` + `v_jobs_full` closed this year);
 * labor and materials are generated as a deterministic share of revenue.
 */
export const simulatedJobPnl = once(async (): Promise<JobPnlPayload> => {
  const [jobs, invoices] = await Promise.all([loadTable('v_jobs_full'), loadTable('job_invoices')]);
  const year = Number(nyDate().slice(0, 4));

  const revenueByJob = new Map<string, number>();
  for (const r of invoices) {
    const jn = str(r.job_number);
    if (!jn) continue;
    revenueByJob.set(jn, (revenueByJob.get(jn) ?? 0) + (num(r.invoice_subtotal) || num(r.total_invoiced)));
  }

  const rows: JobPnlRow[] = [];
  for (const j of jobs) {
    // Spec §3: restricted client-side to Dash Status "Closed" with a close date
    // in the current year. `status_changed_at` is the CRM's close stamp.
    if (str(j.status) !== 'closed') continue;
    const closedAt = str(j.status_changed_at) || str(j.invoiced_at);
    if (!closedAt || Number(closedAt.slice(0, 4)) !== year) continue;
    const jn = str(j.job_number);
    const revenue = round2(revenueByJob.get(jn) ?? (num(j.invoice_subtotal) || num(j.total_invoiced)));
    if (revenue <= 0) continue;

    const laborShare = seededFloat(`pnl|${jn}|labor`, 0.18, 0.34);
    const matShare = seededFloat(`pnl|${jn}|mat`, 0.14, 0.32);
    const laborCost = round2(revenue * laborShare);
    const materialCost = round2(revenue * matShare);
    const grossProfit = round2(revenue - laborCost - materialCost);

    rows.push({
      jobNumber: jn,
      customer: str(j.customer_name),
      division: str(j.division),
      revenue,
      laborCost,
      materialCost,
      grossProfit,
      grossMarginPct: revenue > 0 ? round2((grossProfit / revenue) * 100) : 0,
      closedAt,
      lines: [
        { kind: 'revenue', description: `Invoice — ${str(j.division)} services`, amount: revenue },
        { kind: 'labor', description: `Timesheet labor (jobcode ${jn})`, amount: laborCost },
        { kind: 'material', description: 'Materials & subcontractors (bills)', amount: materialCost },
      ],
    });
  }

  rows.sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Revenue and the closed-job list are real (job_invoices + v_jobs_full, status closed this year); '
      + 'labor and material cost are generated as a deterministic share of each job\'s revenue, because the demo '
      + 'has no TSheets timesheets or QuickBooks bills behind it.',
    jobs: rows,
  };
});

// ── /api/qb-payments ────────────────────────────────────────────────────────

export interface PaymentsPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  /** 'YYYY-MM' → cash in, by payment TxnDate. */
  byMonth: { month: string; amount: number }[];
  ytdTotal: number;
}

/**
 * `/api/qb-payments` — real cash-in by payment TxnDate. Dash has no payment
 * dates at all, which is why the Measurables "Revenue Collected" row needs
 * QuickBooks. Derived here from `v_jobs_full.total_collected` spread over the
 * invoice month, so it ties to the Collections tab.
 */
export const simulatedPayments = once(async (): Promise<PaymentsPayload> => {
  const jobs = await loadTable('v_jobs_full');
  const byMonth = new Map<string, number>();
  for (const j of jobs) {
    const collected = num(j.total_collected);
    if (collected <= 0) continue;
    const invoiced = str(j.invoiced_at);
    if (!invoiced) continue;
    // Cash lands after the invoice: 0–2 months later, deterministic per job.
    const lag = seededInt(`pay|${str(j.job_number)}`, 0, 2);
    const d = new Date(invoiced);
    d.setUTCMonth(d.getUTCMonth() + lag);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    byMonth.set(key, (byMonth.get(key) ?? 0) + collected);
  }
  const year = nyDate().slice(0, 4);
  const rows = [...byMonth.entries()]
    .map(([month, amount]) => ({ month, amount: round2(amount) }))
    .sort((a, b) => a.month.localeCompare(b.month));
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Cash-in by month is v_jobs_full.total_collected placed 0–2 months after each job\'s invoice date '
      + '(deterministic per job) — the snapshot has no payment TxnDate.',
    byMonth: rows,
    ytdTotal: round2(rows.filter((r) => r.month.startsWith(year)).reduce((s, r) => s + r.amount, 0)),
  };
});

// ── /api/qb-invoiced-jobs ───────────────────────────────────────────────────

export interface InvoicedJobsPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  /** Σ markdown (sales-discount) by item account, YTD. */
  markdownByAccount: { account: string; amount: number }[];
  markdownTotal: number;
  grossInvoiced: number;
  /** Average markdown as a % of gross invoiced (the Measurables row). */
  averageMarkdownPct: number;
}

/**
 * `/api/qb-invoiced-jobs` — markdown by item account, i.e. the adjuster
 * write-downs behind the Measurables "Average Markdown" row. Uses the same
 * Sales-Discount lines as `/api/qb-accounting`.
 */
export const simulatedInvoicedJobs = once(async (): Promise<InvoicedJobsPayload> => {
  const today = nyDate();
  const months = monthRange(`${today.slice(0, 4)}-01`, today.slice(0, 7));
  const acc = await pnlAccountsFor(months, 'ytd');
  const markdownByAccount = acc.discounts
    .map((l) => ({ account: l.name, amount: round2(l.actual) }))
    .sort((a, b) => a.amount - b.amount);
  const markdownTotal = round2(markdownByAccount.reduce((s, r) => s + r.amount, 0));
  return {
    ok: true,
    simulated: true,
    demo_note:
      'Markdown by item account is the Sales Discount lines of the simulated P&L — each keeps the ratio '
      + `budget-2026.json gives it against its parent income account; the income behind it is ${DEMO_NOTE_TAIL}`,
    markdownByAccount,
    markdownTotal,
    grossInvoiced: acc.grossInvoiced,
    averageMarkdownPct: acc.grossInvoiced > 0
      ? round2(Math.abs(markdownTotal / acc.grossInvoiced) * 100) : 0,
  };
});

// ── /api/gmb-reviews ────────────────────────────────────────────────────────

export interface GmbReviewsPayload {
  ok: true;
  simulated: true;
  demo_note: string;
  locations: { name: string; total: number; thisMonth: number; rating: number }[];
  totalThisMonth: number;
}

/**
 * `/api/gmb-reviews` — the Google Business Profile review counts for BOTH
 * locations (the Measurables "Reviews" row). The `gmb_reviews` table is empty in
 * the snapshot, so the counts are generated deterministically per month.
 */
export const simulatedGmbReviews = once(async (): Promise<GmbReviewsPayload> => {
  const rows = await loadTable('gmb_reviews');
  const monthKey = nyDate().slice(0, 7);
  const locations = ['PDQ Restoration — Pompton Plains', 'PDQ Restoration — Wayne'].map((name) => {
    const real = rows.filter((r) => str(r.location_name) === name);
    const thisMonth = real.length > 0
      ? real.filter((r) => str(r.created_at).slice(0, 7) === monthKey).length
      : seededInt(`gmb|${name}|${monthKey}`, 3, 11);
    const total = real.length > 0 ? real.length : seededInt(`gmb|${name}|total`, 180, 420);
    return { name, total, thisMonth, rating: round2(seededFloat(`gmb|${name}|rating`, 4.6, 5.0)) };
  });
  return {
    ok: true,
    simulated: true,
    demo_note: rows.length > 0
      ? 'Review counts come from the gmb_reviews table.'
      : 'The gmb_reviews table is empty in the snapshot, so per-location review counts are generated deterministically.',
    locations,
    totalThisMonth: locations.reduce((s, l) => s + l.thisMonth, 0),
  };
});

/** The fiscal-year month keys the budget file covers — shared by several tabs. */
export const fyMonths = once(async (): Promise<string[]> => {
  const doc = await budgetDoc();
  return monthRange(doc.fyStart.slice(0, 7), doc.fyEnd.slice(0, 7));
});
